import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import styled from "styled-components";
import { motion, useMotionValue, useTransform, useAnimation } from "framer-motion";
import Colors from "../../../ui/global/Colors";
import Button from "../../../ui/components/Button";
import Fonts from "../../../ui/global/Fonts";
import { HistoryEntry as HistoryEntryProps } from "../utils/useArtHistory";

const Container = styled.div<{ $width: number; $height: number }>`
    min-width: ${props => props.$width}px;
    height: 100%;
    min-height: ${props => props.$height}px;
    display: flex;
    position: relative;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    margin: auto;
    padding: 0;
    user-select: none;
    
    // Debug borders
    // border: 1px solid red;

    // transform-origin: top center;
    // transition: transform 0.2s ease-in-out;
    // &:hover {
    //     transform: scale(1.2);
    // }

    &::before, &::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        width: 40px;
        pointer-events: none;
    }

    &::before {
        left: 0;
        background: linear-gradient(to right, ${Colors.background}, transparent);
    }

    &::after {
        right: 0;
        background: linear-gradient(to left, ${Colors.background}, transparent);
    }
`;

const DraggableContainer = styled(motion.div)<{ $height: number; $contentWidth: number }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    position: absolute;
    width: ${props => Math.max(props.$contentWidth, 100)}px;
    height: 100%;
    min-height: ${props => props.$height}px;
    cursor: grab;
    user-select: none;
    pointer-events: all;
    left: 0;

    // Debug borders
    // border: 1px solid cyan;
    
    &:active {
        cursor: grabbing;
    }
`;

const HistoryEntry = styled(motion.div)`
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow: visible;
    user-select: none;
    pointer-events: all;
`;

const ImagesInner = styled(motion.div)<{ $isSelected?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: space-between;
    justify-content: flex-end;
    background-color: ${Colors.transparent};
    border-radius: 2px;
    // border: ${props => props.$isSelected ? `1px solid ${Colors.black}` : `1px solid ${Colors.transparent}`};
    object-fit: contain;
    user-select: none;
    pointer-events: none;
`;

const Image = styled.img<{ $size: number }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${props => props.$size}px;
    height: ${props => props.$size}px;
    border-radius: 2px;
    border: 1px solid ${Colors.transparent};
    object-fit: contain;
    margin: auto;
    user-select: none;
`;

const BlankImage = styled.div<{ $size: number }>`
    width: ${props => props.$size}px;
    height: ${props => props.$size}px;
    background-color: ${Colors.white};
    border-radius: 2px;
    border: 1px solid ${Colors.transparent};
    margin: auto;
    user-select: none;
`;

const Blank = styled.div<{ $size: number }>`
    width: ${props => props.$size}px;
    height: ${props => props.$size}px;
    background-color: ${Colors.transparent};
    user-select: none;
`;

const Description = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 14.4px;
    max-width: 100%;
    font-size: 12px;
    line-height: 1.2em;
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    color: ${Colors.grey6};
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

interface ToolbarHistoryProps {
    history: HistoryEntryProps[];
    currentIndex: number;
    config: any;
    onSelectState: (state: HistoryEntryProps) => void;
}

const ToolbarHistory: React.FC<ToolbarHistoryProps> = ({ history, currentIndex, config, onSelectState }) => {
    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Check if Ctrl/Cmd key is pressed
            const isCtrlOrCmd = e.metaKey || e.ctrlKey;
            
            if (isCtrlOrCmd && e.key.toLowerCase() === 'z') {
                e.preventDefault(); // Prevent browser's default undo/redo
                
                if (e.shiftKey) {
                    // Redo - go forward in history
                    if (currentIndex < history.length - 1) {
                        onSelectState(history[currentIndex + 1]);
                    }
                } else {
                    // Undo - go back in history
                    if (currentIndex > 0) {
                        onSelectState(history[currentIndex - 1]);
                    }
                }
            }
        };

        // Add event listener
        window.addEventListener('keydown', handleKeyDown);

        // Cleanup
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [history, currentIndex, onSelectState]);
    // Constants
    const NUM_TO_SHOW = 20;
    const ENTRY_WIDTH = 22;
    const ENTRY_SPACING = 0;
    const PANEL_WIDTH = NUM_TO_SHOW * (ENTRY_WIDTH + ENTRY_SPACING);
    const PANEL_HEIGHT = ENTRY_WIDTH * 2; 

    // Refs
    const containerRef = useRef<HTMLDivElement>(null);
    const lastUpdateTimeRef = useRef<number>(0);
    const [isDragging, setIsDragging] = useState(false);
    
    // Motion values
    const x = useMotionValue(0);
    const controls = useAnimation();
    
    // Calculate the total width needed for all entries
    const totalContentWidth = useMemo(() => {
        return history.length * (ENTRY_WIDTH + ENTRY_SPACING);
    }, [history.length]);
    
    // Calculate the center position of each entry
    const entryPositions = useMemo(() => {
        return history.map((_, index) => index * (ENTRY_WIDTH + ENTRY_SPACING));
    }, [history.length]);
    
    // Calculate the position to center the current entry
    const centerCurrentEntry = useCallback(() => {
        if (history.length === 0) return 0;
        
        // Get the position of the current entry
        const currentEntryPosition = entryPositions[currentIndex];
        // Center of the panel
        const panelCenter = PANEL_WIDTH / 2;
        // Position to center the current entry
        return panelCenter - currentEntryPosition - ENTRY_WIDTH / 2;
    }, [currentIndex, history.length, entryPositions, PANEL_WIDTH]);
    
    // Update position when current index changes
    useEffect(() => {
        if (history.length === 0) return;
        
        const targetX = centerCurrentEntry();
        
        // console.log('setting current entry to position: ', targetX);
        
        controls.start({
            x: targetX,
            transition: { type: "spring", stiffness: 400, damping: 40 }
        });
    }, [currentIndex, controls, history.length, centerCurrentEntry]);
    
    // Ensure proper positioning when history changes
    useEffect(() => {
        if (history.length === 0) return;
        
        // Slight delay to ensure the DOM has updated
        const timer = setTimeout(() => {
            const targetX = centerCurrentEntry();
            x.set(targetX);
        }, 50);
        
        return () => clearTimeout(timer);
    }, [history.length, x, centerCurrentEntry]);
    
    // Handle drag
    const handleDrag = async (event: any, info: any, end?: boolean) => {
        if (history.length === 0) return;
        
        const velocity = info.velocity.x;
        const currentPosition = x.get();
        
        // Calculate which entry is closest to the center of the panel
        const panelCenter = PANEL_WIDTH / 2;
        
        // Find the entry closest to the center
        let closestIndex = 0;
        let minDistance = Number.MAX_VALUE;
        
        entryPositions.forEach((position, index) => {
            // Calculate the absolute position of this entry with the current drag
            const entryCenter = position + currentPosition + ENTRY_WIDTH / 2;
            // Calculate how far this entry is from the center of the panel
            const distanceFromCenter = Math.abs(panelCenter - entryCenter);
            
            if (distanceFromCenter < minDistance) {
                minDistance = distanceFromCenter;
                closestIndex = index;
            }
        });
        
        // If we're ending the drag, snap to center the closest entry
        if (end) {
            const targetX = panelCenter - entryPositions[closestIndex] - ENTRY_WIDTH / 2;
            
            await controls.start({
                x: targetX,
                transition: {
                    type: "spring",
                    velocity: velocity * 0.2,
                    stiffness: 400,
                    damping: 40
                }
            });
            
            // console.log('setting current entry to snap point: ', targetX);
            
            // Always update state when drag ends
            if (onSelectState && history[closestIndex]) {
                onSelectState(history[closestIndex]);
                // console.log('Selected entry:', closestIndex);
                lastUpdateTimeRef.current = Date.now();
            }
        } else {
            // During drag, throttle updates to once every 100ms
            const now = Date.now();
            if (now - lastUpdateTimeRef.current >= 100) {
                if (onSelectState && history[closestIndex]) {
                    onSelectState(history[closestIndex]);
                    // console.log('Selected entry (throttled):', closestIndex);
                    lastUpdateTimeRef.current = now;
                }
            }
        }
    };
    
    // Calculate drag constraints
    const dragConstraints = useMemo(() => {
        if (history.length <= 1) {
            // For a single entry, center it and don't allow dragging
            return { left: centerCurrentEntry(), right: centerCurrentEntry() };
        }
        
        // Calculate the minimum and maximum x positions
        // The minimum x is when the last entry is centered at the panel center
        const minX = PANEL_WIDTH / 2 - (history.length - 1) * (ENTRY_WIDTH + ENTRY_SPACING) - ENTRY_WIDTH / 2;
        // The maximum x is when the first entry is centered at the panel center
        const maxX = PANEL_WIDTH / 2 - ENTRY_WIDTH / 2;
        
        // console.log('Drag constraints:', { minX, maxX, totalContentWidth });
        
        // Ensure the constraints are valid numbers
        return { 
            left: isFinite(minX) ? minX : -totalContentWidth, 
            right: isFinite(maxX) ? maxX : 0 
        };
    }, [history.length, centerCurrentEntry, PANEL_WIDTH, ENTRY_WIDTH, ENTRY_SPACING, totalContentWidth]);

    return (
        <Container id="history-panel" ref={containerRef} $width={PANEL_WIDTH} $height={PANEL_HEIGHT}>
            <DraggableContainer id="history-draggable-container"
                drag={history.length > 1 ? "x" : false}
                dragConstraints={dragConstraints}
                dragElastic={0.1}
                dragMomentum={true}
                onDragStart={() => setIsDragging(true)}
                onDrag={(e, info) => handleDrag(e, info)}
                onDragEnd={(e, info) => {
                    handleDrag(e, info, true);
                    setIsDragging(false);
                }}
                animate={controls}
                style={{ x }}
                initial={{ x: centerCurrentEntry() }}
                $height={PANEL_HEIGHT}
                $contentWidth={totalContentWidth}
            >
                {history.map((entry, index) => (
                    config.SHOW_DRAW_HISTORY_USER_IMAGE && (
                        <HistoryEntry
                            key={entry.iterationId}
                            style={{ 
                                left: entryPositions[index],
                                width: ENTRY_WIDTH,
                                height: ENTRY_WIDTH * 2,
                                x: 0 // Each entry stays in its fixed position
                            }}
                            onClick={() => onSelectState(entry)}
                        >
                            <ImagesInner $isSelected={history.findIndex(h => h.iterationId === entry.iterationId) === currentIndex && isDragging}>
                                {config.SHOW_DRAW_HISTORY_USER_IMAGE && (
                                    entry.userPng ? (
                                        <Image
                                            key={entry.createdAt+'userPng'}
                                            src={entry.userPng}
                                            title={`v${index + 1}`}
                                            $size={ENTRY_WIDTH}
                                        />
                                    ) : (
                                        <BlankImage key={entry.createdAt+'userPng'} $size={ENTRY_WIDTH} />
                                    )
                                )}
                                {entry.aiImage ? (
                                    <Image
                                        key={entry.createdAt+'aiImage'}
                                        src={entry.aiImage}
                                        title={`v${index + 1}`}
                                        $size={ENTRY_WIDTH}
                                    />
                                ) : (
                                    <Blank key={entry.createdAt+'aiImage'} $size={ENTRY_WIDTH} />
                                )}
                            </ImagesInner>
                            {/* <Description>{history.findIndex(h => h.iterationId === entry.iterationId) + 1}</Description> */}
                            {config.SHOW_DRAW_HISTORY_DESCRIPTION && (
                                <Description key={entry.createdAt+'description'}>
                                    {entry.description}
                                </Description>
                            )}
                        </HistoryEntry>
                    )
                ))}
            </DraggableContainer>
        </Container>
    );
};

export default ToolbarHistory;